/**
 * LOGIN OFFLINE — verifikasi NIK+PIN di HP saat server/jaringan belum siap.
 * ============================================================================
 * Setiap login ONLINE yang sukses menyimpan "verifier" (PBKDF2 dari PIN + salt
 * acak) beserta data user terakhir. PIN TIDAK pernah disimpan. Saat server tak
 * terjangkau, login dicocokkan ke verifier ini sehingga user tetap bisa masuk
 * dan absen (absen masuk antrean, dikirim saat server kembali).
 *
 * Batasan (sengaja):
 *  - Hanya untuk NIK yang PERNAH login online di HP/browser ini.
 *  - Verifier kedaluwarsa OFFLINE_MAX_AGE_DAYS sejak login online terakhir.
 *  - Salah PIN offline MAX_FAILED_ATTEMPTS kali → verifier NIK itu dihapus
 *    (wajib login online lagi). PIN 4 digit mudah ditebak bila dicoba terus.
 *  - Butuh WebCrypto (HTTPS / localhost). Tanpa itu login offline tak tersedia.
 * ============================================================================
 */

const STORAGE_KEY = "hris_offline_auth";
const OFFLINE_MAX_AGE_DAYS = 14;
const MAX_FAILED_ATTEMPTS = 5;
const MAX_ENTRIES = 3; // HP bersama: simpan beberapa akun terakhir saja
const PBKDF2_ITERATIONS = 150000;

const normNik = (nik) => String(nik || "").trim().toLowerCase();

const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export function isOfflineLoginSupported() {
  return typeof crypto !== "undefined" && !!crypto.subtle && typeof localStorage !== "undefined";
}

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch { /* storage penuh/terblokir: login offline tak tersedia */ }
}

async function derive(pin, salt) {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(String(pin)), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    key,
    256
  );
  return toB64(bits);
}

/** Simpan verifier setelah login ONLINE sukses. Gagal diam-diam (fitur tambahan). */
export async function saveOfflineCredential(nik, pin, user) {
  if (!isOfflineLoginSupported() || !user?.id) return;
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const hash = await derive(pin, salt);
    const entries = readAll();
    entries[normNik(nik)] = { salt: toB64(salt), hash, user, savedAt: Date.now(), failed: 0 };
    // Buang akun paling lama bila melebihi batas.
    const keys = Object.keys(entries).sort((a, b) => (entries[b].savedAt || 0) - (entries[a].savedAt || 0));
    keys.slice(MAX_ENTRIES).forEach((k) => delete entries[k]);
    writeAll(entries);
  } catch { /* abaikan */ }
}

/** Perbarui data user tersimpan (mis. profil terbaru) tanpa mengubah verifier. */
export function updateOfflineUser(user) {
  if (!user?.id) return;
  const entries = readAll();
  const key = Object.keys(entries).find((k) => entries[k]?.user?.id === user.id);
  if (!key) return;
  entries[key] = { ...entries[key], user };
  writeAll(entries);
}

export function removeOfflineCredential(nik) {
  const entries = readAll();
  delete entries[normNik(nik)];
  writeAll(entries);
}

/**
 * Cocokkan NIK+PIN ke verifier lokal.
 * @returns {Promise<{ok: true, user: object} | {ok: false, reason: "unsupported"|"not_found"|"expired"|"wrong_pin"|"locked", remaining?: number}>}
 */
export async function verifyOfflineCredential(nik, pin) {
  if (!isOfflineLoginSupported()) return { ok: false, reason: "unsupported" };
  const key = normNik(nik);
  const entries = readAll();
  const entry = entries[key];
  if (!entry?.hash || !entry?.salt || !entry?.user) return { ok: false, reason: "not_found" };

  if (Date.now() - (entry.savedAt || 0) > OFFLINE_MAX_AGE_DAYS * 86400000) {
    removeOfflineCredential(key);
    return { ok: false, reason: "expired" };
  }

  let hash;
  try {
    hash = await derive(pin, fromB64(entry.salt));
  } catch {
    return { ok: false, reason: "unsupported" };
  }

  if (hash !== entry.hash) {
    const failed = (entry.failed || 0) + 1;
    if (failed >= MAX_FAILED_ATTEMPTS) {
      removeOfflineCredential(key);
      return { ok: false, reason: "locked" };
    }
    entries[key] = { ...entry, failed };
    writeAll(entries);
    return { ok: false, reason: "wrong_pin", remaining: MAX_FAILED_ATTEMPTS - failed };
  }

  if (entry.failed) {
    entries[key] = { ...entry, failed: 0 };
    writeAll(entries);
  }
  return { ok: true, user: entry.user };
}
